import axios from 'axios';
import { ActionTree } from 'vuex';
import service from '../../services/entity.service';
import { RootState } from '../../store/store';
import { mutationSetTeams } from '../../store/mutations/archive.mutations';
import { actionSetSettings } from '../../store/actions/document-settings.actions';
import { IEntityState } from './entity.types';
import { EEntityAction, EEntityMutation } from './entity.constants';
import { translate } from '@/helpers/translation.helper';
import { IDocumentSettings } from '@/types/DocumentSettings';
import { IEntity } from '@/entity/types/entity';
import { IUser } from '@/types/User';
import { ITeam } from '@/types/Team';
import { IPlanStatus } from '@/types/plan-status';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';
import { SelectEntityService } from '@lib-shared/api/select-entities/select-entities.service';
import bugsnagClient from '@lib-shared/bugsnag';
import { ISelectEntity } from '@lib-shared/types/select-entity';

export const actions: ActionTree<IEntityState, RootState> = {
  async [EEntityAction.LoadEntitySettings]({ commit, dispatch }) {
    commit(EEntityMutation.SetIsLoading, true);
    const {
      data: { entity },
    } = await axios.get(`/get/settings/entity`);
    if (entity) {
      const documentSettings: IDocumentSettings = {
        allowDocumentDownload: entity.document_download,
        allowSigningForward: entity.is_signing_forward,
        requireSelfSign: entity.is_selfsign,
        verifySmsOpen: entity.verify_sms_open,
        verifySmsSign: entity.verify_sms_sign,
        isSigningBiometric: entity.is_signing_biometric,
        isSigningInitials: entity.is_signing_initials,
        isSigningEId: entity.is_signing_eid,
        isSigningWrite: entity.is_signing_write,
        hasDisabledComments: entity.is_disable_comment,
        doIdentifyRecipient: entity.is_identify_recipient,
        hasEsignConsent: entity.is_esign_consent,
        documentUniqueId: entity.document_unique_id,
      };

      await dispatch(`documentSettings/${actionSetSettings}`, documentSettings, {
        root: true,
      });

      const entityData: IEntity = {
        entityId: entity.id,
        logoUrl: '',
        theme: null,
      };
      commit(EEntityMutation.SetEntitySettings, entityData);
      commit(EEntityMutation.SetIsLoading, false);
    }
  },
  async [EEntityAction.LoadUsers]({ dispatch }) {
    try {
      const users: IUser[] = await service.fetchUsers();
      dispatch(EEntityAction.LoadUsersSuccess, users);
      dispatch(EEntityAction.LoadEntity);
    } catch (error) {
      dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('could-not-fetch-users'), {
        root: true,
      });
    }
  },
  [EEntityAction.LoadUsersSuccess]({ commit }, users: IUser[]) {
    commit(EEntityMutation.SetUsers, users);
  },
  async [EEntityAction.LoadTeams]({ dispatch }) {
    dispatch(EEntityAction.SetIsLoadingTeams, true);
    try {
      const teams: ITeam[] = await service.fetchTeams();
      dispatch(EEntityAction.LoadTeamsSuccess, teams);
    } catch (e) {
      dispatch(EEntityAction.LoadTeamsFailed);
    }
  },
  [EEntityAction.LoadTeamsSuccess]({ commit, dispatch }, teams: ITeam[]) {
    commit(mutationSetTeams, teams);
    dispatch(EEntityAction.SetIsLoadingTeams, false);
  },
  [EEntityAction.LoadTeamsFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-load-teams', {
        root: true,
      })
    );
    dispatch(EEntityAction.SetIsLoadingTeams, false);
  },
  [EEntityAction.SetIsLoadingTeams]({ commit }, payload: boolean) {
    commit(EEntityMutation.SetIsLoadingTeams, payload);
  },
  async [EEntityAction.LoadPlanStatus]({ dispatch }) {
    try {
      const planStatus: IPlanStatus = await service.fetchPlanStatus();
      dispatch(EEntityAction.LoadPlanStatusSuccess, planStatus);
    } catch (error) {
      dispatch(EEntityAction.LoadPlanStatusFailed);
    }
  },
  [EEntityAction.LoadPlanStatusSuccess]({ commit }, planStatus: IPlanStatus) {
    commit(EEntityMutation.SetPlanStatus, planStatus);
  },
  async [EEntityAction.LoadEntity]({ dispatch }) {
    try {
      const entity: IEntity = await service.fetchEntity();
      dispatch(EEntityAction.LoadEntitySuccess, entity);
    } catch (e) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('could-not-load-entity', {
          root: true,
        })
      );
    }
  },
  [EEntityAction.LoadEntitySuccess]({ commit }, entity: IEntity) {
    commit(EEntityMutation.SetEntity, entity);
  },
  async [EEntityAction.LoadEntities]({ dispatch }) {
    try {
      const entities: ISelectEntity[] = await SelectEntityService.entities();
      dispatch(EEntityAction.LoadEntitiesSuccess, entities);
    } catch (e) {
      dispatch(EEntityAction.LoadEntitiesFailed, e);
    }
  },
  async [EEntityAction.LoadEntitiesSuccess]({ commit }, entities: ISelectEntity[]) {
    commit(EEntityMutation.SetEntities, entities);
  },
  async [EEntityAction.LoadEntitiesFailed]({ dispatch }, e) {
    if (bugsnagClient) {
      bugsnagClient.notify(e);
    }
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-load-entities', {
        root: true,
      })
    );
  },
  async [EEntityAction.SelectEntity]({ dispatch }, entityId: string) {
    try {
      await SelectEntityService.selectEntity(entityId);
      dispatch(EEntityAction.SelectEntitySuccess);
    } catch (e) {
      dispatch(EEntityAction.SelectEntityFailed, e);
    }
  },
  [EEntityAction.SelectEntitySuccess]() {
    window.open('/', '_self');
  },
  [EEntityAction.SelectEntityFailed]({ dispatch }, e: Error) {
    if (bugsnagClient) {
      bugsnagClient.notify(e);
    }
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-load-the-selected-entity', {
        root: true,
      })
    );
  },
  [EEntityAction.AuthEntity](_, { id, auth }: ISelectEntity) {
    window.open(`/auth/${auth}/${id}${auth === 'saml' ? '/sso' : ''}`, '_self');
  },
};
